'use client';

import { useMemo, useState, type ReactNode } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import NextLink from 'next/link';
import { useParams } from 'next/navigation';
import { slate } from '@/lib/colours';
import { Box, CircularProgress, Link, Stack, Typography } from '@/primitives';
import { formatDate } from '@/lib/dateTime';
import { SEARCH_PARAM } from '@/constants/searchParams';
import { SEARCH_TYPE } from '@/constants/searchType';
import { buildSocietySearchHref } from '@/lib/publicSearchPaths';
import { useSocietyDateFormat } from '@/hooks/publicSearch/useSocietyDisplayOptions';
import type { GetSalesQuery } from '@/publicSearch/graphql/queries.generated';
import { ExternalLink, LinkifiedText } from './LinkifiedText';
import {
  linkifyField,
  linkifyText,
  resolveSaleInfoLink,
  saleEnquirySubject,
  type SaleInfoLink,
} from './saleLinks';

type SaleDetail = NonNullable<GetSalesQuery['sales']['items'][number]>;

type SaleInfoPanelProps = {
  loading: boolean;
  saleDetail: SaleDetail | null;
};

// Shared columns so the contact row lines up under Location / Sale Date / Lots.
const rowSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
  gap: 2,
} as const;

function InfoField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: slate[400], fontSize: '12px', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography component="div" sx={{ fontSize: '14px' }}>
        {children}
      </Typography>
    </Box>
  );
}

function SaleInfoLinkItem({ link }: { link: SaleInfoLink }) {
  const params = useParams<{ orgName?: string }>();

  if (link.kind === 'external') {
    return <ExternalLink href={link.href}>{link.text}</ExternalLink>;
  }
  if (link.kind === 'sale' && params?.orgName) {
    // i4 "#<saleNo>" link to another catalogue, e.g. a reference sire list.
    const href = buildSocietySearchHref(
      params.orgName,
      SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS,
      new URLSearchParams({ [SEARCH_PARAM.SALE_ID]: String(link.saleId) }),
    );
    return (
      <Link component={NextLink} href={href} prefetch={false}>
        {link.text}
      </Link>
    );
  }
  return (
    <Box component="span">
      <LinkifiedText segments={linkifyText(link.text)} />
    </Box>
  );
}

export default function SaleInfoPanel({
  loading,
  saleDetail,
}: SaleInfoPanelProps) {
  const [expanded, setExpanded] = useState(true);
  const dateFormat = useSocietyDateFormat();

  // BLS-406: contact details and links i4 listed under the lots.
  const { email, homepage, links } = useMemo(() => {
    const subject = saleEnquirySubject(saleDetail?.saleName);
    return {
      email: linkifyField(saleDetail?.email, subject),
      homepage: linkifyField(saleDetail?.homepage),
      links: [
        [saleDetail?.extraInfoUrl1, saleDetail?.extraInfoLabel1],
        [saleDetail?.extraInfoUrl2, saleDetail?.extraInfoLabel2],
        [saleDetail?.extraInfoUrl3, saleDetail?.extraInfoLabel3],
      ]
        .map(([url, label]) =>
          resolveSaleInfoLink(url, label, saleDetail?.saleId),
        )
        .filter((link): link is SaleInfoLink => link !== null),
    };
  }, [saleDetail]);
  const hasContact =
    email.length > 0 || homepage.length > 0 || links.length > 0;

  if (loading) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          py: 2,
          mb: 1.5,
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (!saleDetail) return null;

  return (
    <Box
      sx={{
        mb: 1.5,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          minHeight: 48,
          px: 2,
          gap: 1.5,
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setExpanded((prev) => !prev);
            }
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            py: 1,
            cursor: 'pointer',
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
            {saleDetail.saleName}
          </Typography>
          {saleDetail.saleDateDescription && (
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: '0.8rem',
                mt: 0.25,
              }}
            >
              {saleDetail.saleDateDescription}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          aria-label={
            expanded ? 'Collapse sale details' : 'Expand sale details'
          }
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            flexShrink: 0,
            ml: 0.5,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Stack>
      <Collapse in={expanded}>
        <Stack sx={{ gap: 2, px: 2, pt: 0, pb: 2 }}>
          <Stack sx={rowSx}>
            {saleDetail.saleLocation && (
              <InfoField label="Location">{saleDetail.saleLocation}</InfoField>
            )}
            {saleDetail.saleDate && (
              <InfoField label="Sale Date">
                {formatDate(new Date(saleDetail.saleDate), dateFormat)}
              </InfoField>
            )}
            {saleDetail.lotCount != null && (
              <InfoField label="Lots">{saleDetail.lotCount}</InfoField>
            )}
          </Stack>
          {hasContact && (
            <Stack sx={rowSx}>
              {email.length > 0 && (
                <InfoField label="Email">
                  <LinkifiedText segments={email} />
                </InfoField>
              )}
              {homepage.length > 0 && (
                <InfoField label="Website">
                  <LinkifiedText segments={homepage} />
                </InfoField>
              )}
              {links.length > 0 && (
                <InfoField label="Links">
                  <Stack sx={{ gap: 0.5 }}>
                    {links.map((link, index) => (
                      <SaleInfoLinkItem key={index} link={link} />
                    ))}
                  </Stack>
                </InfoField>
              )}
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}
